import { type ElementType, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface Props {
  value: string
  onChange: (next: string) => void
  as: ElementType
  readOnly?: boolean
  className?: string
}

export function TextBlockBase({ value, onChange, as: Tag, readOnly = false, className }: Props) {
  const ref = useRef<HTMLElement | null>(null)
  const initialRef = useRef<string>(value)

  useEffect(() => {
    if (ref.current && ref.current.textContent !== value) {
      ref.current.textContent = value
    }
    initialRef.current = value
  }, [value])

  return (
    <Tag
      ref={ref as React.Ref<HTMLElement>}
      contentEditable={!readOnly}
      suppressContentEditableWarning
      onBlur={() => {
        const next = ref.current?.textContent ?? ''
        if (next !== initialRef.current) onChange(next)
      }}
      onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Escape') {
          if (ref.current) ref.current.textContent = initialRef.current
          ref.current?.blur()
        }
      }}
      className={cn(
        'outline-none',
        // override the parent's cursor:pointer with text caret on the
        // contenteditable so the user gets the editing affordance whenever
        // their pointer is over the text content.
        !readOnly && 'cursor-text',
        className,
      )}
    >
      {value}
    </Tag>
  )
}
